import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutDashboard, Loader2, Monitor, Plus, Star, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { useBoardTemplates, useBoards, useCreateBoard, useFavouriteBoard, type DashboardListItem } from '@/api/boards';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { dashboardDisplayPath, dashboardPath } from '@/lib/routes';
import { DASHBOARDS_MENU, isNavLink } from '@/lib/navigation';
import { cn } from '@/lib/utils';

/**
 * Dashboards (`/dashboards`): yours, ones shared with you, and ones shared with everyone. New
 * dashboards start blank or from a template; starred ones also appear in the sidebar.
 */
export default function DashboardsPage() {
  const { data, isLoading, error } = useBoards();
  const [newOpen, setNewOpen] = useState(false);
  const all = data?.dashboards ?? [];
  const sections: { title: string; items: DashboardListItem[]; empty: string }[] = [
    { title: 'Starred', items: all.filter((d) => d.favourite), empty: '' },
    { title: 'Mine', items: all.filter((d) => d.via === 'mine'), empty: 'You have no dashboards yet. Start one from a template or blank.' },
    { title: 'Shared with me', items: all.filter((d) => d.via === 'shared'), empty: 'Nothing shared with you directly.' },
    { title: 'Shared with everyone', items: all.filter((d) => d.via === 'everyone'), empty: 'Nobody has shared a dashboard with everyone yet.' },
  ];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
      <PageHeader title="Dashboards"
        subtitle="Build your own from any Cosmos metric, share them, and put them on a wall display."
        actions={<Button size="sm" onClick={() => setNewOpen(true)}><Plus className="h-3.5 w-3.5" /> New dashboard</Button>} />
      {isLoading ? <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        : error ? <div className="mt-4 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
        : sections.map((s) => (s.items.length === 0 && !s.empty ? null : (
          <section key={s.title} className="mt-5">
            <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{s.title}{s.items.length > 0 && ` · ${s.items.length}`}</h2>
            {s.items.length === 0 ? <p className="text-sm text-muted-foreground">{s.empty}</p> : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {s.items.map((d) => <BoardTile key={`${s.title}:${d.id}`} d={d} />)}
              </div>
            )}
          </section>
        )))}
      <section className="mt-6">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Built-in dashboards</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {DASHBOARDS_MENU.filter((g) => g.title !== 'Custom').map((g) => (
            <div key={g.title} className="rounded-lg border border-border bg-card p-3 shadow-sm">
              <div className="mb-1 text-xs font-semibold text-muted-foreground">{g.title}</div>
              <ul className="space-y-0.5">
                {g.items.filter(isNavLink).map((i) => (
                  <li key={i.to}><Link to={i.to} className="block rounded px-1 py-0.5 text-sm text-foreground hover:bg-muted">{i.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Their numbers are also available as cards for your own dashboards.</p>
      </section>
      <NewDashboardDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}

function BoardTile({ d }: { d: DashboardListItem }) {
  const fav = useFavouriteBoard();
  return (
    <div className="group relative flex flex-col rounded-lg border border-border bg-card p-4 shadow-sm hover:border-ring/50">
      <div className="flex items-start gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand"><LayoutDashboard className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <Link to={dashboardPath(d.id)} title={d.name} className="block truncate font-medium text-foreground after:absolute after:inset-0">{d.name}</Link>
          <div className="truncate text-xs text-muted-foreground">{d.access.isOwner ? 'Yours' : d.ownerName} · {d.cards} card{d.cards === 1 ? '' : 's'}</div>
        </div>
        <button type="button" onClick={() => fav.mutate({ id: d.id, on: !d.favourite })} className="relative z-10 text-muted-foreground hover:text-warning"
          title={d.favourite ? 'Unstar' : 'Star (adds it to the sidebar)'} aria-label={d.favourite ? 'Unstar' : 'Star'}>
          <Star className={cn('h-4 w-4', d.favourite && 'fill-warning text-warning')} />
        </button>
      </div>
      {d.description && <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{d.description}</p>}
      <div className="mt-auto flex items-center gap-2 pt-3 text-[11px] text-muted-foreground">
        {d.everyone !== 'none' && <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> everyone</span>}
        <span>Updated {fmtRelative(d.updatedAt)}</span>
        <Link to={dashboardDisplayPath(d.id)} className="relative z-10 ml-auto inline-flex items-center gap-1 hover:text-foreground" title="Open as a full-screen wall display"><Monitor className="h-3 w-3" /> Display</Link>
      </div>
    </div>
  );
}

function NewDashboardDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const templates = useBoardTemplates(open);
  const create = useCreateBoard();
  const [name, setName] = useState('');
  const [template, setTemplate] = useState<string>('blank');
  const chosen = templates.data?.templates.find((t) => t.id === template);

  const go = () => {
    const n = name.trim() || chosen?.name || 'New dashboard';
    create.mutate(template === 'blank'
      ? { name: n, definition: { cards: [], refreshSeconds: 300, client: { mode: 'picker' } } }
      : { name: n, template },
    {
      onSuccess: (r) => { onOpenChange(false); setName(''); setTemplate('blank'); navigate(dashboardPath(r.dashboard.id)); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to create'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New dashboard</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={chosen?.name ?? 'Name'} maxLength={120} autoFocus
            className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-ring" />
          <div className="space-y-1.5">
            {[{ id: 'blank', name: 'Blank', description: 'Start empty and add your own cards.', cards: 0 }, ...(templates.data?.templates ?? [])].map((t) => (
              <button key={t.id} type="button" onClick={() => setTemplate(t.id)}
                className={cn('w-full rounded-md border px-3 py-2 text-left', template === t.id ? 'border-ring bg-brand-soft' : 'border-border hover:bg-muted/50')}>
                <div className="font-medium text-foreground">{t.name}{t.cards > 0 && <span className="ml-2 text-xs font-normal text-muted-foreground">{t.cards} cards</span>}</div>
                <div className="text-xs text-muted-foreground">{t.description}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">New dashboards are private until you share them.</p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={go} disabled={create.isPending}>{create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
